import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { useSnackbar } from '@/context/SnackbarContext';
import { API_ENDPOINTS } from '@/constants';
import { GLOBAL_PROMPT_QUESTIONS_QUERY_KEY } from '@/hooks/useFetchGlobalPromptQuestions';
import { GLOBAL_PROMPT_INSIGHTS_QUERY_KEY } from '@/hooks/useFetchGlobalPromptInsights';
import type { SaveGlobalQuestion } from '../types';

const useSaveGlobalPromptQuestions = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (questions: SaveGlobalQuestion[]) => {
            const response = await api.post(API_ENDPOINTS.GLOBAL_PROMPT_QUESTIONS, { questions });

            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GLOBAL_PROMPT_QUESTIONS_QUERY_KEY });
            // The insights endpoint derives its question list from the same data, so its
            // cached response is now stale — refetch it too, else the Insights tab keeps
            // showing "No prompt questions yet" until staleTime expires or a hard refresh.
            queryClient.invalidateQueries({ queryKey: GLOBAL_PROMPT_INSIGHTS_QUERY_KEY });
            showSnackbar('Questions saved successfully', 'success');
        },
        onError: error => {
            showSnackbar(getErrorMessage(error) ?? 'Failed to save questions', 'error');
        },
    });
};

export default useSaveGlobalPromptQuestions;
